import React, {useState, useEffect} from "react";
import {NotificationManager} from "react-notifications";
import {useParams} from "react-router-dom";
import Select from "react-select";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import useApi from "../Api/Api";
import SideLabelInput from "../Components/SideLabelInput";
import Button from "../Components/Button";

const EditUser = ({handleShowForm, getUser}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const params = useParams();
  const api = useApi();
  const [user, setUser] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "user",
  });

  const handleChange = ({target: {id, value}}) => {
    setUser({
      ...user,
      [id]: value,
    });
  };

  const userRoles = [{label: "User", value: "user"}, {label: "Manager", value: "manager"}, {
    label: "Admin",
    value: "administrator"
  }]

  const handleRoleChange = (e) => {
    setUser({
      ...user,
      role: e.value,
    });
  };

  const editedUser = {
    id: user.id,
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role,
  };

  const handleSubmit = (event) => {
    setIsFetching(true);
    api
      .put(`/users/${editedUser.id}`, {
        user: {
          ...editedUser,
          artist_users_attributes: [
            ...artistIdsToSave.map((artistId) => ({
              artist_id: artistId,
            })),
            ...artistUserIdsToRemove.map((each) => ({
              id: each.artistUserId,
              _destroy: "1",
            })),
          ],
        },
      })
      .then((response) => {
        NotificationManager.success("User successfully updated", "Success", 2000);
        handleShowForm();
        getUser();
        return response.data;
      })
      .catch((error) => {
        setIsFetching(false);
        setErrorMessage(error.response.data);
      })
      .finally(() => {
      });
    event.preventDefault();
  };

  const [allArtists, setAllArtists] = useState([]);
  const [artistUsers, setArtistUsers] = useState([]);

  const remainingOptions = allArtists
    .filter(({id}) => !artistUsers.find(({artist_id}) => id === artist_id))
    .map(({id, name}) => ({label: name, value: id}));

  const artistUsersOptions = artistUsers
    .map((each) => ({
      label: each.artist.name,
      value: each.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const [artistIdsToSave, setArtistIdsToSave] = useState([]);
  const [artistUserIdsToRemove, setArtistUserIdsToRemove] = useState([]);

  const handleArtistIdsToAdd = (e) => {
    let value = Array.from(e, (option) => option.value);
    setArtistIdsToSave(value);
  };

  const handleArtistIdsToRemove = (event, artistUser) => {
    setArtistUserIdsToRemove([
      ...artistUserIdsToRemove,
      {artistUserId: artistUser.value},
    ]);
    event.preventDefault();
  };

  useEffect(() => {
    api.get(`/artists`).then((r) => {
      setAllArtists(r.data);
    });
    api.get(`/artist_users?user_id=${params.userId}`).then((r) => {
      setArtistUsers(r.data);
    });
    api.get(`/users/${params.userId}`).then((r) => {
      setUser(r.data);
    });
  }, [params.userId, api]);

  return (
    <div>
      <div className="-mt-20 side-modal">
        <div className="flex-col">
          <header className="py-6 px-4 text-lg mb-2 bg-blue-500 text-white">
            <button className="float-right -mt-4" onClick={handleShowForm}>
              <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
            </button>
            <div>Edit User</div>
            <div className="text-sm">
              You can modify this information later if needed
            </div>
          </header>
          <div className="mt-8 px-4 tracking-normal	font-medium">
            User Information
          </div>
          <hr className="my-3 mx-4"></hr>
          <form className="mx-4" id="submit" onSubmit={handleSubmit}>
            <div className="my-5">
              <SideLabelInput
                id="email"
                type="email"
                label="Email"
                value={user.email}
                name="email"
                onChange={handleChange}
                disabled={isFetching}
                required
              />
              {errorMessage?.errors?.email && (
                <div className="text-red-500 pt-2">
                  Email {errorMessage.errors.email}
                </div>
              )}
            </div>
            <div className="my-5">
              <SideLabelInput
                id="first_name"
                type="first_name"
                label="First Name"
                value={user.first_name}
                name="first_name"
                onChange={handleChange}
                disabled={isFetching}
              />
            </div>
            <div className="my-5">
              <SideLabelInput
                id="last_name"
                type="last_name"
                label="Last Name"
                value={user.last_name}
                name="last_name"
                onChange={handleChange}
                disabled={isFetching}
              />
            </div>
            <div className="my-5">
              <label>Role</label>
              <div>
                <Select
                  className="text-xs font-light"
                  value={userRoles.find((option) => option.value === user.role)}
                  options={userRoles}
                  onChange={handleRoleChange}
                />
              </div>
            </div>
            {user.role !== "administrator" && user.role !== "manager" ? (
              <>
                {artistUsersOptions.length === 0 ? (
                  <div className="mt-8 text-sm">
                    {user.email} currently manages no artists
                  </div>
                ) : (
                  <div className="mt-8 text-sm">
                    {user.email} currently manages the following artists:
                  </div>
                )}
                {artistUsersOptions
                  .filter(
                    ({value}) =>
                      !artistUserIdsToRemove.find(
                        ({artistUserId}) => value === artistUserId
                      )
                  )
                  .map((each) => (
                    <div key={each.value} id={each.value}>
                      <span className="px-2 my-3 text-xs">{each.label}</span>
                      <button
                        className="mr-4 float-right"
                        onClick={(event) =>
                          handleArtistIdsToRemove(event, each)
                        }
                      >
                        <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
                      </button>
                    </div>
                  ))}
                {artistUsersOptions.length === 0 ? (
                  <div className="mt-8 mb-4 text-sm">
                    Select artists to be managed by {user.email}
                  </div>
                ) : (
                  <div className="mt-8 mb-4 text-sm">
                    Select more artists to be managed by {user.email}
                  </div>
                )}
                <Select
                  className="text-sm"
                  isMulti
                  options={remainingOptions}
                  onChange={handleArtistIdsToAdd}
                />
              </>
            ) : (
              <div className="m-2 mt-8 text-sm">
                {user.email} is an admin and has access to all artists
              </div>
            )}
            <hr className="my-3"></hr>
            <div className="flex justify-center mt-8">
              {errorMessage?.error && (
                <div className="text-red-500 pb-2">{errorMessage.error}</div>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <Button
                text={isFetching ? `Loading ${"\u00A0"}` : "Save"}
                type="submit"
                form="submit"
                value="Submit"
                disabled={isFetching}
                inProgress={isFetching}
                className="button"
              ></Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
